import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';

// ============================================================
// Wing-tip contrails — fading white lines from wing tips
// ============================================================

const TRAIL_LENGTH = 80;

// Reusable vector
const _worldPos = new THREE.Vector3();

class Trail {
  readonly line: Line2;
  private readonly posBuffer: Float32Array;
  private readonly colorBuffer: Float32Array;
  private readonly geometry: LineGeometry;
  private readonly offset: THREE.Vector3;
  private initialized = false;

  constructor(wingOffset: THREE.Vector3, lineWidth: number) {
    this.offset = wingOffset;
    this.posBuffer = new Float32Array(TRAIL_LENGTH * 3);
    this.colorBuffer = new Float32Array(TRAIL_LENGTH * 3); // RGB per vertex

    // Pre-compute fade gradient (white at tip → black at tail) — never changes
    for (let i = 0; i < TRAIL_LENGTH; i++) {
      const fade = 1 - i / TRAIL_LENGTH;
      this.colorBuffer[i * 3] = fade;
      this.colorBuffer[i * 3 + 1] = fade;
      this.colorBuffer[i * 3 + 2] = fade;
    }

    this.geometry = new LineGeometry();
    this.geometry.setPositions(this.posBuffer);
    this.geometry.setColors(this.colorBuffer);

    const material = new LineMaterial({
      color: 0xffffff,
      linewidth: lineWidth,
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
      depthWrite: false,
      worldUnits: true,
    });

    this.line = new Line2(this.geometry, material);
    this.line.frustumCulled = false;
    this.line.computeLineDistances();
  }

  update(aircraft: THREE.Object3D): void {
    _worldPos.copy(this.offset);
    aircraft.localToWorld(_worldPos);

    if (!this.initialized) {
      for (let i = 0; i < TRAIL_LENGTH; i++) {
        this.posBuffer[i * 3] = _worldPos.x;
        this.posBuffer[i * 3 + 1] = _worldPos.y;
        this.posBuffer[i * 3 + 2] = _worldPos.z;
      }
      this.initialized = true;
    }

    // Shift points back
    for (let i = TRAIL_LENGTH - 1; i > 0; i--) {
      this.posBuffer[i * 3] = this.posBuffer[(i - 1) * 3];
      this.posBuffer[i * 3 + 1] = this.posBuffer[(i - 1) * 3 + 1];
      this.posBuffer[i * 3 + 2] = this.posBuffer[(i - 1) * 3 + 2];
    }

    // Newest point
    this.posBuffer[0] = _worldPos.x;
    this.posBuffer[1] = _worldPos.y;
    this.posBuffer[2] = _worldPos.z;

    this.geometry.setPositions(this.posBuffer);
    this.line.computeLineDistances();
  }

  dispose(): void {
    this.geometry.dispose();
    (this.line.material as THREE.Material).dispose();
  }
}

export class WingTrails {
  private readonly leftTrail: Trail;
  private readonly rightTrail: Trail;

  constructor(
    scene: THREE.Scene,
    leftWingTip: THREE.Vector3 = new THREE.Vector3(-3, 0, 0),
    rightWingTip: THREE.Vector3 = new THREE.Vector3(3, 0, 0),
    lineWidth = 0.4,
  ) {
    this.leftTrail = new Trail(leftWingTip, lineWidth);
    this.rightTrail = new Trail(rightWingTip, lineWidth);

    scene.add(this.leftTrail.line);
    scene.add(this.rightTrail.line);
  }

  update(aircraft: THREE.Object3D): void {
    this.leftTrail.update(aircraft);
    this.rightTrail.update(aircraft);
  }

  dispose(): void {
    this.leftTrail.line.removeFromParent();
    this.leftTrail.dispose();
    this.rightTrail.line.removeFromParent();
    this.rightTrail.dispose();
  }
}
